import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleDamageDetailsDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import DamageDetailsEditDialog from './DamageDetailsEditDialog'

export default function DamageDetailsCard() {
  const { vehicleGuid, damageGuid } = useParams<{ vehicleGuid: string; damageGuid: string }>()

  const [isSavingDamageDetails, setIsSavingDamageDetails] = useState<boolean>(false)
  const [isGettingDamageDetails, setIsGettingDamageDetails] = useState<boolean>(false)
  const [damageDetailsEditDialogOpen, setDamageDetailsEditDialogOpen] = useState<boolean>(false)
  const [damageDetails, setDamageDetails] = useState<VehicleDamageDetailsDto>(new VehicleDamageDetailsDto())

  useEffect(() => {
    fetchDamageDetails()
  }, [vehicleGuid, damageGuid])

  const fetchDamageDetails = () => {
    if (vehicleGuid && damageGuid) {
      setIsGettingDamageDetails(true)
      controllers
        .getDamageDetails(vehicleGuid, damageGuid)
        .then(response => {
          setDamageDetails(response.value)
        })
        .finally(() => {
          setIsGettingDamageDetails(false)
        })
    }
  }

  return (
    <>
      <DamageDetailsEditDialog
        initialValues={damageDetails}
        isLoading={isSavingDamageDetails}
        onClose={() => setDamageDetailsEditDialogOpen(false)}
        onSave={values => {
          setIsSavingDamageDetails(true)
          controllers
            .updateDamageDetails(vehicleGuid!, damageGuid!, values)
            .then(response => {
              if (response?.value) {
                setDamageDetails(response.value)
                enqueueSnackbar('Damage Details Saved Successfully!', { variant: 'success' })
              }
            })
            .catch(error => {
              enqueueSnackbar('Failed to save damage details', { variant: 'error' })
              console.error(error)
            })
            .finally(() => {
              setIsSavingDamageDetails(false)
              setDamageDetailsEditDialogOpen(false)

              // **Fetch latest data to ensure it's updated in the backend**
              fetchDamageDetails()
            })
        }}
        open={damageDetailsEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Damage Details
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  onClick={() => {
                    setDamageDetailsEditDialogOpen(true)
                  }}
                  variant='text'
                  endIcon={<Edit />}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingDamageDetails && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingDamageDetails && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Damage Description' value={damageDetails.damageDescription} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Status' value={damageDetails.damageStatus} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Edit Button**
                
Clicking the Edit Button opens the Damage Details Edit Dialog.

**Damage Description:**

Displays the damage description entered when the damage was reported.

**Status:** 

Displays the status of the damaged vehicle.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
